import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Skeleton } from 'antd'
import { PQueryBuilderProvider } from '@pushly/aqe'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder/pquery-builder'
import { useService } from '@pushly/aqe/lib/hooks'
import type { PQueryBuilderField } from '@pushly/models/lib/interfaces/pquery-builder-field'
import type { SegmentQueryField } from '@pushly/models/lib/interfaces/segment-query-field'
import type { PQueryDocument } from '@pushly/models/lib/interfaces/pquery-document'
import { SegmentQueryFieldsService } from '../../../services/segment-query-fields.service'
import { useSegmentQueryFields } from '../../segment-query-builder/use-segment-query-fields'
import {
    SEGMENT_VALUE_INPUTS,
    SEGMENT_VALUE_VALIDATORS,
    useSegmentValueSummaries,
} from '../../segment-query-builder/value-inputs'
import { SegmentValueInputProvider } from '../../segment-query-builder/value-inputs/segment-value-input-provider'
import { useSegmentValueInputContext } from '../../segment-query-builder/value-inputs/segment-value-input-context'

const NO_CATALOG: SegmentQueryField[] = []

/**
 * The document to open the canvas on.
 *
 * A journey stores `{}` from the moment entry criteria are switched on until the first condition
 * is saved, and the builder reads a document with no condition tree as one it cannot represent —
 * so opening straight onto a stored `{}` would report the criteria as unsupported and refuse to
 * edit a journey that simply has none yet.
 *
 * The group is an `or`, so conditions added inside it read as alternatives, and groups still
 * combine with AND at the root. Safe to seed because a group that stays empty is dropped rather
 * than stored, so the scaffolding never reaches the wire.
 */
function openableDocument(value: PQueryDocument | undefined): Record<string, unknown> {
    const stored = (value ?? {}) as Record<string, unknown>

    return Object.keys(stored).length ? stored : { and: [{ or: [] }] }
}

export interface JourneyEntryCriteriaProps {
    domainId: number
    /** The stored criteria. An absent or empty document opens an empty canvas. */
    value: PQueryDocument | undefined
    onChange: (criteria: PQueryDocument) => void
    disabled?: boolean
    /** Substitutes for the catalog read. Must keep its identity for as long as this is mounted. */
    fieldsService?: Pick<SegmentQueryFieldsService, 'getDomainFieldsCached'>
}

/**
 * The conditions a subscriber must satisfy to enter a journey.
 *
 * The same builder the segment screen uses, over the same catalog, narrowed to the fields the
 * catalog says entry criteria may be built from. Entry criteria are the same properties with the
 * same operators — what differs is the evaluator — so nothing about the vocabulary is restated
 * here. A field becomes available to journeys by being marked in the catalog, not by a change to
 * this component.
 *
 * The document this reads and writes is the one the entry evaluator already consumes.
 */
export function JourneyEntryCriteria({
    domainId,
    value,
    onChange,
    disabled,
    fieldsService,
}: JourneyEntryCriteriaProps) {
    const resolvedFieldsSvc = useService(SegmentQueryFieldsService)
    const fieldsSvc = fieldsService ?? resolvedFieldsSvc

    const [catalog, setCatalog] = useState<SegmentQueryField[] | null>(null)

    useEffect(() => {
        let live = true

        // A failed read settles as an empty catalog rather than staying null, so this never waits
        // forever — the criteria render with nothing to choose from, which is visible, instead of
        // a skeleton that never resolves.
        fieldsSvc
            .getDomainFieldsCached(domainId)
            .then((fields) => live && setCatalog(fields))
            .catch(() => live && setCatalog(NO_CATALOG))

        return () => {
            live = false
        }
    }, [fieldsSvc, domainId])

    const fields = useSegmentQueryFields(catalog ?? NO_CATALOG, { surface: 'journey_entry' })

    // The document as it currently stands, for the renderers that need to read the rest of the
    // query rather than only their own condition. Held in a ref because they read it when the
    // reader opens a row, not when it changes.
    const document = useRef<Record<string, unknown>>(openableDocument(value))
    const readDocument = useCallback(() => document.current, [])

    const handleRuleCommit = useCallback(
        (next: Record<string, unknown>) => {
            document.current = next
            onChange(next as PQueryDocument)
        },
        [onChange],
    )

    // The builder cannot offer a property picker before it knows the properties, and mounting it
    // early would give it the document to parse against an empty catalog — every condition would
    // read as one on a field that does not exist.
    if (catalog === null) {
        return <Skeleton active={true} title={false} paragraph={{ rows: 2 }} />
    }

    // A catalog that offers this surface nothing is a server that has not been told about it yet.
    // Rendering the builder anyway would be an empty property picker with no explanation, and
    // saying so is what keeps a deploy in the wrong order from reading as a broken screen.
    if (fields.length === 0) {
        return (
            <Alert
                type="info"
                title="Entry criteria are unavailable for this domain."
                description="No subscriber attributes are currently offered for journey entry. Any criteria already saved on this journey are unchanged."
            />
        )
    }

    return (
        <SegmentValueInputProvider level="domain" domainId={domainId} fields={fields} getDocument={readDocument}>
            <JourneyEntryCriteriaStore
                value={document.current}
                fields={fields}
                disabled={disabled}
                onRuleCommit={handleRuleCommit}
            />
        </SegmentValueInputProvider>
    )
}

interface StoreProps {
    value: Record<string, unknown>
    fields: PQueryBuilderField[]
    disabled?: boolean
    onRuleCommit: (next: Record<string, unknown>) => void
}

/**
 * The query store, standing inside the value context rather than around it.
 *
 * A row summary is a plain function the builder calls with a condition, so anything it needs from
 * outside that condition — a prompt's name, for one — has to be closed over when the registry of
 * summaries is built. Building it here is what lets a summary re-read as the vocabularies arrive
 * instead of naming an id for the whole session.
 */
function JourneyEntryCriteriaStore({ value, fields, disabled, onRuleCommit }: StoreProps) {
    const valueSummaries = useSegmentValueSummaries()

    // The same read the segment builder hands the builder, and correct here for the same reason:
    // the catalog names these properties, so a field whose values are only partly carried inline
    // is searched at the source under the name the store holds them by.
    const { loadFieldValues } = useSegmentValueInputContext()

    return (
        <PQueryBuilderProvider
            value={value}
            fields={fields}
            valueInputs={SEGMENT_VALUE_INPUTS}
            valueSummaries={valueSummaries}
            valueValidators={SEGMENT_VALUE_VALIDATORS}
            loadFieldValues={loadFieldValues}
            constraints={{ allowGroupDescriptions: false }}
            onRuleCommit={onRuleCommit}
        >
            <PQueryBuilder disabled={disabled} />
        </PQueryBuilderProvider>
    )
}
